import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '../../Icons';
import { useRouting } from '../../../contexts/RoutingContext';
import styles from '../AppContent.module.css';
import localStyles from './Research.module.css';

interface ResearchItem {
  id: string;
  title: string;
  description: string;
  type: 'pdf' | 'youtube' | 'presentation' | 'notes';
  url: string;
  thumbnail: string;
}

const RESEARCH_ITEMS: ResearchItem[] = [
  {
    id: 'ieee-paper',
    title: 'IEEE Microgrid Conference Paper',
    description: 'Published research on demand flexibility and distributed energy resources at IEEE International Conference on DC Microgrids.',
    type: 'pdf',
    url: '/research/IEEE_paper.pdf',
    thumbnail: '/research/thumbnails/ieee-paper.png',
  },
  {
    id: 'lbl-presentation',
    title: 'Technical Communication Presentation on Load Shifting',
    description: 'YouTube presentation covering research conducted at Lawrence Berkeley National Laboratory on grid flexibility.',
    type: 'youtube',
    url: 'https://www.youtube.com/embed/g6mifG99Rv0',
    thumbnail: 'https://img.youtube.com/vi/g6mifG99Rv0/hqdefault.jpg',
  },
  {
    id: 'cse269-presentation',
    title: 'Approximation Algorithms Presentation',
    description: 'Research presentation on approximation algorithms and optimization techniques.',
    type: 'presentation',
    url: '/research/CSE269_Presentation.pdf',
    thumbnail: '/research/thumbnails/cse269-presentation.png',
  },
  {
    id: 'cse185e-presentation',
    title: 'Technical Communication Load Shifting Slides',
    description: 'Technical presentation slides on load shifting strategies and energy optimization.',
    type: 'presentation',
    url: '/research/CSE185E_Presentation.pdf',
    thumbnail: '/research/thumbnails/cse185e-presentation.png',
  },
  {
    id: 'aditya-sam',
    title: 'Demand Flexibility Research Paper',
    description: 'Research paper on demand flexibility, energy systems, and smart grid technologies.',
    type: 'pdf',
    url: '/research/Aditya_Sam.pdf',
    thumbnail: '/research/thumbnails/aditya-sam.png',
  },
  {
    id: 'cse269-notes',
    title: 'Approximation Algorithms Notes',
    description: 'Comprehensive notes on approximation algorithms, complexity theory, and optimization.',
    type: 'notes',
    url: '/research/CSE_269_notes.pdf',
    thumbnail: '/research/thumbnails/cse269-notes.png',
  },
  {
    id: 'hpwh-report',
    title: 'Advanced M&V for Scope 2 GHG Emissions',
    description: 'Technical report on advanced measurement and verification methods for Scope 2 greenhouse gas emissions.',
    type: 'pdf',
    url: '/research/20240722163127000_6fac730f-a22f-4213-9467-ea63f7316bab.pdf',
    thumbnail: '/research/thumbnails/hpwh-report.png',
  },
  {
    id: 'dasht-i-leili',
    title: 'Dasht-i-Leili',
    description: 'An essay examining the 2001 Dasht-i-Leili massacre in Afghanistan through the lens of state secrecy and the politics of knowledge. Written for JRLC 140: Secrecy, Knowledge, and Politics.',
    type: 'pdf',
    url: '/research/Dasht-i-Leili.pdf',
    thumbnail: '/research/thumbnails/dasht-i-leili.png',
  },
];

export const Research: React.FC = () => {
  const { getInitialSubPath, setSubPath } = useRouting();
  const initializedRef = useRef(false);

  // Initialize from URL sub-path on mount
  const [selectedItem, setSelectedItem] = useState<ResearchItem | null>(() => {
    const subPath = getInitialSubPath('research');
    if (subPath) {
      return RESEARCH_ITEMS.find(item => item.id === subPath) || null;
    }
    return null;
  });

  // Sync URL when selection changes (skip initial mount)
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      return;
    }
    setSubPath('research', selectedItem?.id || null);
  }, [selectedItem, setSubPath]);

  if (selectedItem) {
    return (
      <div className={styles.appContainer}>
        <div className={styles.toolbar}>
          <button
            className={styles.toolbarButton}
            onClick={() => setSelectedItem(null)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M10 2 L4 8 L10 14 L10 10 L14 10 L14 6 L10 6 Z"/>
            </svg>
            Back to Research
          </button>
          <button
            className={styles.toolbarButton}
            onClick={() => window.open(selectedItem.url, '_blank')}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M14 16H2C0.9 16 0 15.1 0 14V2C0 0.9 0.9 0 2 0H6V2H2V14H14V10H16V14C16 15.1 15.1 16 14 16Z"/>
              <path d="M8 0V2H12.59L5.29 9.29L6.71 10.71L14 3.41V8H16V0H8Z"/>
            </svg>
            Open in New Tab
          </button>
        </div>

        <div className={localStyles.viewerContainer}>
          {selectedItem.type === 'youtube' ? (
            <iframe
              src={selectedItem.url}
              title={selectedItem.title}
              className={localStyles.viewer}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <iframe
              src={selectedItem.url}
              title={selectedItem.title}
              className={localStyles.viewer}
            />
          )}
        </div>

        <div className={styles.statusBar}>
          <span>{selectedItem.title}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.appContainer}>
      <div className={styles.contentPanel}>
        <div className={localStyles.header}>
          <Icon name="research" size={48} />
          <div className={localStyles.headerText}>
            <h1 className={localStyles.title}>Research & Publications</h1>
            <p className={localStyles.subtitle}>
              Academic papers, presentations, and research materials from my work in energy systems and machine learning.
            </p>
          </div>
        </div>

        <div className={localStyles.grid}>
          {RESEARCH_ITEMS.map((item) => (
            <button
              key={item.id}
              className={`${localStyles.card} ${item.type === 'youtube' || item.type === 'presentation' ? localStyles.cardLandscape : ''}`}
              onClick={() => setSelectedItem(item)}
            >
              <div className={`${localStyles.cardThumbnail} ${item.type === 'youtube' || item.type === 'presentation' ? localStyles.thumbnailLandscape : ''}`}>
                <img
                  src={item.thumbnail}
                  alt={item.title}
                  className={localStyles.thumbnailImage}
                />
              </div>
              <div className={localStyles.cardContent}>
                <h3 className={localStyles.cardTitle}>{item.title}</h3>
                <span className={`${localStyles.cardType} ${localStyles[`type${item.type.charAt(0).toUpperCase() + item.type.slice(1)}`]}`}>
                  {item.type === 'youtube' ? 'Video' : item.type === 'presentation' ? 'Presentation' : item.type === 'notes' ? 'Notes' : 'PDF'}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className={styles.statusBar}>
        {RESEARCH_ITEMS.length} items
      </div>
    </div>
  );
};

export default Research;
